import { List as ImmutableList, Map as ImmutableMap, fromJS } from 'immutable';
import { chartToTableReport } from 'reporting-data/lib/convertToTableReport';
import { isConfigReport } from 'reporting-data/tsTypes/reportTypes';
// @ts-expect-error Untyped import
import { setLimit } from 'reporting-action-components/lib/report-logic';
import { clearReportTemplateAndId } from 'reporting-data/report/reportSetters';
import { getMetricsFromSelectedTableMetricValues } from '../../../reportEditors/TableMetricEditor/tableMetricEditorUtils';
import { overwriteFilterValuesInCheckboxTable, updateReportFilterForCheckboxTableConfig } from './filterUtils';
import { getCustomFiltersFromTableReport, getFilterValuesFromFilterForCheckboxTable, getIsUAReportWithBreakdownDimension, hasCheckboxTableSupport, processListOfCheckedRowsFromFilterValues } from './utils';
import { getDimensions, getReportConfig } from 'reporting-data/report/configReportGetters';
import { setDimensions } from 'reporting-data/report/configReportSetters';
const updateFilterInReportForToggleSeries = ({
  report,
  selectedFilterName,
  onPreviewReportChange,
  updatedFilter
}) => {
  const updatedFilterValues = getFilterValuesFromFilterForCheckboxTable(updatedFilter);
  const customFilters = getCustomFiltersFromTableReport(report);
  const selectedFilterIndex = customFilters.findIndex(filter => !!filter && filter.get('property') === selectedFilterName);
  let newReport = report;
  const isUpdatedFilterEmpty = updatedFilterValues.length === 0;
  const isFilterInReport = selectedFilterIndex !== -1;
  if (isUpdatedFilterEmpty && isFilterInReport) {
    newReport = report.updateIn(['config', 'filters', 'custom'], originalFilters => originalFilters.delete(selectedFilterIndex));
  } else if (!isUpdatedFilterEmpty) {
    newReport = report.updateIn(['config', 'filters', 'custom'], originalCustomFilters => !isFilterInReport ? originalCustomFilters.push(updatedFilter) : originalCustomFilters.set(selectedFilterIndex, updatedFilter));
  }
  onPreviewReportChange(newReport);
};
export const configureToggleSeries = ({
  report,
  selectedFilterName,
  onPreviewReportChange,
  dimensionMetaData,
  tracker
}) => id => {
  const series = String(id);
  const updatedFilter = updateReportFilterForCheckboxTableConfig(report, selectedFilterName, series, dimensionMetaData);
  tracker === null || tracker === void 0 || tracker.track('srvCheckboxTableCheckboxToggle', {
    isAllFilters: false,
    filterName: selectedFilterName
  });
  updateFilterInReportForToggleSeries({
    report,
    selectedFilterName,
    onPreviewReportChange,
    updatedFilter
  });
};
export const configureToggleAllSeries = ({
  report,
  selectedFilterName,
  onPreviewReportChange,
  dimensionMetaData,
  tracker
}) => ids => {
  const series = ids.map(id => String(id));
  const updatedFilter = overwriteFilterValuesInCheckboxTable(report, selectedFilterName, series, dimensionMetaData);
  tracker === null || tracker === void 0 || tracker.track('srvCheckboxTableCheckboxToggle', {
    isAllFilters: true
  });
  updateFilterInReportForToggleSeries({
    report,
    selectedFilterName,
    onPreviewReportChange,
    updatedFilter
  });
};
const getBaseTableReport = (report, rowLimit, onRowLimitChange, is2DSupported, selectedDimension) => {
  const selectedTableMetrics = getMetricsFromSelectedTableMetricValues(report);
  const updatedTableReport = chartToTableReport(report, selectedTableMetrics);

  /**
   * if report is the same either the report was a table or was unable to be converted.
   * For both cases, we have some kind of report we do not want to show in addition to the original report
   */
  if (report === updatedTableReport || !isConfigReport(updatedTableReport)) {
    return null;
  }
  let updatedReport = updatedTableReport.setIn(['config', 'configType'], 'AGGREGATION').update('displayParams', (prevDisplayParams = ImmutableMap()) => prevDisplayParams.merge(fromJS({
    rowsPerPage: {
      onChange: onRowLimitChange
    },
    showTotals: true
  })));

  // If 2D is supported, we just maintain the dimensions from the parent report
  if (!is2DSupported) {
    const updatedConfig = setDimensions(getReportConfig(updatedReport), ImmutableList([selectedDimension]));
    updatedReport = updatedReport.set('config', updatedConfig);
  }
  updatedReport = clearReportTemplateAndId(updatedReport);
  return setLimit(rowLimit, updatedReport);
};
const getTableWithCheckboxes = (originalReport, tableReport, onPreviewReportChange, dimensionMetaData, selectedDimension, tracker) => {
  const isUAReportWithBreakdownDimension = getIsUAReportWithBreakdownDimension(tableReport, selectedDimension);
  const customFilters = getCustomFiltersFromTableReport(tableReport);
  const selectedFilterName = isUAReportWithBreakdownDimension ? 'filters' : selectedDimension;
  const selectedFilterIndex = customFilters.findIndex(filter => !!filter && filter.get('property') === selectedFilterName);
  const toggleSeries = configureToggleSeries({
    report: originalReport,
    selectedFilterName,
    onPreviewReportChange,
    dimensionMetaData,
    tracker
  });
  const toggleAllSeries = configureToggleAllSeries({
    report: originalReport,
    selectedFilterName,
    onPreviewReportChange,
    dimensionMetaData,
    tracker
  });
  const initialValuesForFilter = processListOfCheckedRowsFromFilterValues(tableReport, selectedFilterName, dimensionMetaData);
  return tableReport.setIn(['config', 'filters', 'custom'], selectedFilterIndex !== -1 ? customFilters.delete(selectedFilterIndex) : customFilters).update('displayParams', (prevDisplayParams = ImmutableMap()) => prevDisplayParams.merge(fromJS({
    checked: initialValuesForFilter,
    toggleSeries,
    toggleAllSeries,
    showTotals: prevDisplayParams.get('showTotals'),
    seriesToggleDisabled: () => false
  })));
};
const generateTableReport = (report, onPreviewReportChange, dimensionMetaData, rowLimit, onRowLimitChange, is2DSupported, selectedDimension, tracker) => {
  // We don't care if selectedDimension is undefined if is2DSupported is true,
  // because we'll be pulling the dimension from the parent report
  if (!report || !is2DSupported && !selectedDimension) {
    return null;
  }
  const dimension = is2DSupported ? getDimensions(report).first() : selectedDimension;
  const baseTable = getBaseTableReport(report, rowLimit, onRowLimitChange, is2DSupported, dimension);
  if (!baseTable || !hasCheckboxTableSupport(report) || is2DSupported) {
    return baseTable;
  }
  return getTableWithCheckboxes(report, baseTable, onPreviewReportChange, dimensionMetaData, dimension, tracker);
};
export default generateTableReport;